import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function formatDate(date: string | Date): string {
  return new Date(date).toLocaleDateString('zh-CN', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

export function formatDateTime(date: string | Date): string {
  return new Date(date).toLocaleString('zh-CN', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function formatRelativeTime(date: string | Date): string {
  const now = new Date()
  const then = new Date(date)
  const diff = now.getTime() - then.getTime()
  const seconds = Math.floor(diff / 1000)
  const minutes = Math.floor(seconds / 60)
  const hours = Math.floor(minutes / 60)
  const days = Math.floor(hours / 24)

  if (days > 0) return `${days}天前`
  if (hours > 0) return `${hours}小时前`
  if (minutes > 0) return `${minutes}分钟前`
  return '刚刚'
}

export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

export function debounce<T extends (...args: unknown[]) => unknown>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout | null = null
  return (...args: Parameters<T>) => {
    if (timeout) clearTimeout(timeout)
    timeout = setTimeout(() => func(...args), wait)
  }
}

export function throttle<T extends (...args: unknown[]) => unknown>(
  func: T,
  limit: number
): (...args: Parameters<T>) => void {
  let inThrottle = false
  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      func(...args)
      inThrottle = true
      setTimeout(() => (inThrottle = false), limit)
    }
  }
}

export function generateId(): string {
  // Use crypto.randomUUID for better uniqueness
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID()
  }
  // Fallback for older browsers
  return `${Date.now().toString(36)}-${Math.random().toString(36).substring(2, 11)}`
}

/**
 * 过滤掉 AI 思考过程中的 <thinking> 标签内容
 * 防止模型推理过程泄露到用户界面
 */
export function filterThinkingTags(content: string): string {
  if (!content) return content
  return content
    .replace(/<(think|thinking)>[\s\S]*?<\/\1>/gi, '')
    .replace(/<\/?(?:think|thinking)>/gi, '')
}

const trailingThinkingTagFragments = [
  '</thinking',
  '<thinking',
  '</think',
  '<think',
  '</thin',
  '<thin',
  '</thi',
  '<thi',
  '</th',
  '<th',
  '</t',
  '<t',
  '</',
  '<',
]

export function sanitizeStreamingThinkingContent(content: string): string {
  if (!content) return content

  let sanitizedContent = filterThinkingTags(content)
  const danglingOpenTagMatches = [...sanitizedContent.matchAll(/<(?:think|thinking)>/gi)]

  if (danglingOpenTagMatches.length > 0) {
    const lastDanglingOpenTag = danglingOpenTagMatches[danglingOpenTagMatches.length - 1]
    if (typeof lastDanglingOpenTag.index === 'number') {
      sanitizedContent = sanitizedContent.slice(0, lastDanglingOpenTag.index)
    }
  }

  const lowercaseContent = sanitizedContent.toLowerCase()
  const trailingFragment = trailingThinkingTagFragments.find((fragment) =>
    lowercaseContent.endsWith(fragment)
  )

  if (trailingFragment) {
    return sanitizedContent.slice(0, -trailingFragment.length)
  }

  return sanitizedContent
}

/**
 * 提取 AI 思考过程中的 <thinking> 标签内容
 */
export function extractThinkingContent(content: string): string {
  if (!content) return ''
  const match = content.match(/<(think|thinking)>[\s\S]*?<\/\1>/i)
  return match ? match[0].replace(/<\/?(?:think|thinking)>/gi, '').trim() : ''
}
